import { cleanCnpjOrCpf, formatCnpj, formatCpf } from './document.util';

/**
 * Heurística para máscara em input: após o 11º caractere assume-se CNPJ.
 * CNPJ pode ser alfanumérico (RFB) — por isso conta caracteres `[A-Z0-9]`,
 * nunca só dígitos.
 */
export function isRegisterNumberCnpjMask(
  value: string | null | undefined
): boolean {
  return cleanCnpjOrCpf(value).length > 11;
}

/**
 * Resolve o número de registro correto (CPF ou CNPJ) a partir dos dois campos
 * que a API pode preencher (`cpf` e `idNo`). Prioriza o CPF válido e só usa o
 * `idNo` quando ele for um CNPJ (>11 caracteres) ou quando o CPF estiver vazio.
 * Evita exibir valores incompletos/errados (ex.: "2") quando um dos campos
 * vem preenchido incorretamente. Devolve o valor NORMALIZADO (sem máscara,
 * maiúsculo — CNPJ pode ter letras).
 */
export function resolveRegisterNumber(
  cpf: string | null | undefined,
  idNo: string | null | undefined
): string {
  const cleanCpf = cleanCnpjOrCpf(cpf);
  const cleanIdNo = cleanCnpjOrCpf(idNo);

  const useIdNo =
    cleanIdNo.length > 11 || (cleanCpf.length === 0 && cleanIdNo.length > 0);

  return useIdNo ? cleanIdNo : cleanCpf || cleanIdNo;
}

/**
 * Formata um número de registro (CPF ou CNPJ) para exibição.
 * - Valor 100% numérico (legado): completa zeros à esquerda como antes
 *   (≤11 → CPF `XXX.XXX.XXX-XX`; 12-14 → CNPJ `XX.XXX.XXX/XXXX-XX`).
 * - CNPJ alfanumérico (RFB, com letras): 14 caracteres → `XX.XXX.XXX/XXXX-XX`;
 *   NUNCA completa com zeros (evita fabricar outro documento).
 * - vazio → "NA"; tamanho inesperado → valor normalizado cru.
 */
export function formatRegisterNumber(value: string | null | undefined): string {
  if (!value || value.trim() === '') {
    return 'NA';
  }

  const cleanValue = cleanCnpjOrCpf(value);

  if (cleanValue.length === 0) {
    return 'NA';
  }

  if (/^\d+$/.test(cleanValue) && cleanValue.length <= 14) {
    return cleanValue.length <= 11
      ? formatCpf(cleanValue.padStart(11, '0'))
      : formatCnpj(cleanValue.padStart(14, '0'));
  }

  if (cleanValue.length === 14) {
    return formatCnpj(cleanValue);
  }

  return cleanValue;
}
